import { useState } from 'react';
import { readCSVFile } from '@/utils/export';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle,
  FileSpreadsheet,
  Upload,
  Users,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { BulkAdjustmentRequest, walletService } from '@/services/wallet.service';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';

interface BulkAdjustModalProps {
  open: boolean;
  onClose: () => void;
}

type OperationType = 'add' | 'subtract' | 'set';
type InputMode = 'manual' | 'csv';

interface AdjustmentItem {
  userId: string;
  userName: string;
  userEmail: string;
  currentBalance: number;
  amount: number;
  newBalance: number;
}

/**
 * Bulk Wallet Adjust Modal
 * Allows adjusting multiple wallets at once via manual selection or CSV upload
 */
export function BulkAdjustModal({ open, onClose }: BulkAdjustModalProps) {
  const queryClient = useQueryClient();
  const [inputMode, setInputMode] = useState<InputMode>('manual');
  const [operation, setOperation] = useState<OperationType>('add');
  const [amount, setAmount] = useState<number>(0);
  const [description, setDescription] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [csvAdjustments, setCsvAdjustments] = useState<AdjustmentItem[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [bulkResult, setBulkResult] = useState<{
    success: number;
    failed: number;
    errors: Array<{ userId: string; error: string }>;
  } | null>(null);

  // Fetch all wallets for manual selection
  const { data: walletsData } = useQuery({
    queryKey: ['all-wallets'],
    queryFn: () => walletService.getUserWallets({ maxResultCount: 1000 }),
    enabled: open && inputMode === 'manual',
  });

  const wallets = walletsData?.items || [];

  // Bulk adjust mutation
  const bulkAdjustMutation = useMutation({
    mutationFn: (request: BulkAdjustmentRequest) =>
      walletService.bulkAdjustWallets(request),
    onSuccess: (result) => {
      setBulkResult({
        success: result.successfulAdjustments,
        failed: result.failedAdjustments,
        errors: result.errors || [],
      });
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      queryClient.invalidateQueries({ queryKey: ['wallet-details'] });
      queryClient.invalidateQueries({ queryKey: ['overview-metrics'] });
      toast.success(
        `Bulk adjustment completed: ${result.successfulAdjustments} successful`,
      );
    },
    onError: () => {
      toast.error('Failed to perform bulk adjustment');
    },
  });

  // Handle CSV file upload
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const csvData = await readCSVFile(file);

      // Expected CSV format: userId, amount
      // or: email, amount
      // or: userId, amount, description

      const adjustments: AdjustmentItem[] = [];

      for (const row of csvData) {
        const userId = row.userId || row.email;
        const adjustAmount = parseFloat(row.amount || '0');

        if (!userId || isNaN(adjustAmount)) {
          toast.error(`Invalid CSV row: ${JSON.stringify(row)}`);
          continue;
        }

        // Find wallet by userId or email
        const wallet = wallets.find(
          (w) => w.userId === userId || w.userEmail === userId,
        );

        if (!wallet) {
          toast.error(`User not found: ${userId}`);
          continue;
        }

        const calculatedBalance =
          operation === 'add'
            ? wallet.balance + adjustAmount
            : operation === 'subtract'
              ? wallet.balance - adjustAmount
              : adjustAmount;

        adjustments.push({
          userId: wallet.userId,
          userName: wallet.userName,
          userEmail: wallet.userEmail,
          currentBalance: wallet.balance,
          amount: adjustAmount,
          newBalance: calculatedBalance,
        });
      }

      setCsvAdjustments(adjustments);
      toast.success(`Loaded ${adjustments.length} adjustments from CSV`);
    } catch (error) {
      toast.error('Failed to parse CSV file');
      console.error(error);
    }
  };

  // Generate preview adjustments
  const previewAdjustments = (): AdjustmentItem[] => {
    if (inputMode === 'csv') {
      return csvAdjustments;
    }

    // Manual mode
    return selectedUserIds.map((userId) => {
      const wallet = wallets.find((w) => w.userId === userId)!;
      const calculatedBalance =
        operation === 'add'
          ? wallet.balance + amount
          : operation === 'subtract'
            ? wallet.balance - amount
            : amount;

      return {
        userId: wallet.userId,
        userName: wallet.userName,
        userEmail: wallet.userEmail,
        currentBalance: wallet.balance,
        amount,
        newBalance: calculatedBalance,
      };
    });
  };

  // Execute bulk adjustment
  const handleExecute = () => {
    const adjustments = previewAdjustments();

    if (adjustments.length === 0) {
      toast.error('No adjustments to execute');
      return;
    }

    if (!description.trim() || description.length < 10) {
      toast.error('Please provide a reason (minimum 10 characters)');
      return;
    }

    const request: BulkAdjustmentRequest = {
      adjustments: adjustments.map((adj) => ({
        userId: adj.userId,
        amount: adj.amount,
        description,
        referenceType: 'BulkAdjustment',
        referenceId: `bulk-${Date.now()}`,
      })),
    };

    bulkAdjustMutation.mutate(request);
  };

  // Reset modal
  const handleClose = () => {
    setInputMode('manual');
    setOperation('add');
    setAmount(0);
    setDescription('');
    setSelectedUserIds([]);
    setCsvAdjustments([]);
    setShowPreview(false);
    setBulkResult(null);
    onClose();
  };

  // Toggle user selection
  const toggleUser = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  // Select all users
  const toggleSelectAll = () => {
    if (selectedUserIds.length === wallets.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(wallets.map((w) => w.userId));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Bulk Wallet Adjustment
          </DialogTitle>
          <DialogDescription>
            Adjust multiple user wallets at once via manual selection or CSV
            upload
          </DialogDescription>
        </DialogHeader>

        {bulkResult ? (
          // Results View
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg bg-success/10">
                <div className="flex items-center gap-2 text-success mb-2">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-semibold">Successful</span>
                </div>
                <div className="text-3xl font-bold">{bulkResult.success}</div>
              </div>
              <div className="p-4 border rounded-lg bg-destructive/10">
                <div className="flex items-center gap-2 text-destructive mb-2">
                  <XCircle className="h-5 w-5" />
                  <span className="font-semibold">Failed</span>
                </div>
                <div className="text-3xl font-bold">{bulkResult.failed}</div>
              </div>
            </div>

            {bulkResult.errors.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-semibold text-destructive">Errors:</h3>
                <div className="max-h-[200px] overflow-y-auto space-y-2">
                  {bulkResult.errors.map((error, idx) => (
                    <div
                      key={idx}
                      className="p-2 border rounded bg-destructive/5 text-sm"
                    >
                      <span className="font-medium">{error.userId}:</span>{' '}
                      {error.error}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button onClick={handleClose}>Close</Button>
            </DialogFooter>
          </div>
        ) : showPreview ? (
          // Preview View
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 border rounded-lg bg-warning/10">
              <AlertCircle className="h-5 w-5 text-warning" />
              <p className="text-sm">
                You are about to adjust{' '}
                <strong>{previewAdjustments().length}</strong> wallets. This
                action cannot be undone.
              </p>
            </div>

            <div className="rounded-md border max-h-[400px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead className="text-right">Current</TableHead>
                    <TableHead className="text-center">Change</TableHead>
                    <TableHead className="text-right">New Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewAdjustments().map((adj) => (
                    <TableRow key={adj.userId}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{adj.userName}</p>
                          <p className="text-sm text-muted-foreground">
                            {adj.userEmail}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {adj.currentBalance.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant={
                            operation === 'add'
                              ? 'success'
                              : operation === 'subtract'
                                ? 'destructive'
                                : 'secondary'
                          }
                        >
                          {operation === 'add'
                            ? '+'
                            : operation === 'subtract'
                              ? '-'
                              : '='}
                          {adj.amount.toLocaleString()}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold">
                        {adj.newBalance.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPreview(false)}>
                Back
              </Button>
              <Button
                onClick={handleExecute}
                disabled={bulkAdjustMutation.isPending}
                variant="destructive"
              >
                {bulkAdjustMutation.isPending
                  ? 'Executing...'
                  : 'Confirm & Execute'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          // Configuration View
          <div className="space-y-6">
            {/* Input Mode Selection */}
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant={inputMode === 'manual' ? 'secondary' : 'outline'}
                onClick={() => setInputMode('manual')}
              >
                <Users className="h-4 w-4 mr-2" />
                Manual Selection
              </Button>
              <Button
                variant={inputMode === 'csv' ? 'secondary' : 'outline'}
                onClick={() => setInputMode('csv')}
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                CSV Upload
              </Button>
            </div>

            {inputMode === 'manual' ? (
              // Manual Selection Mode
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Operation</Label>
                    <Select
                      value={operation}
                      onValueChange={(value) =>
                        setOperation(value as OperationType)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="add">Add Coins</SelectItem>
                        <SelectItem value="subtract">Subtract Coins</SelectItem>
                        <SelectItem value="set">Set Balance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Amount</Label>
                    <Input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(Number(e.target.value))}
                      min={0}
                    />
                  </div>
                </div>

                <div>
                  <Label>Reason *</Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Explain why this bulk adjustment is needed (min 10 characters)"
                    rows={3}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label>
                      Select Users ({selectedUserIds.length} selected)
                    </Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={toggleSelectAll}
                    >
                      {selectedUserIds.length === wallets.length
                        ? 'Deselect All'
                        : 'Select All'}
                    </Button>
                  </div>

                  <div className="rounded-md border max-h-[300px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12"></TableHead>
                          <TableHead>User</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {wallets.map((wallet) => (
                          <TableRow key={wallet.userId}>
                            <TableCell>
                              <Checkbox
                                checked={selectedUserIds.includes(
                                  wallet.userId,
                                )}
                                onCheckedChange={() =>
                                  toggleUser(wallet.userId)
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{wallet.userName}</p>
                                <p className="text-sm text-muted-foreground">
                                  {wallet.userEmail}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {wallet.balance.toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </>
            ) : (
              // CSV Upload Mode
              <>
                <div>
                  <Label>Operation</Label>
                  <Select
                    value={operation}
                    onValueChange={(value) =>
                      setOperation(value as OperationType)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="add">Add Coins</SelectItem>
                      <SelectItem value="subtract">Subtract Coins</SelectItem>
                      <SelectItem value="set">Set Balance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Reason *</Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Explain why this bulk adjustment is needed (min 10 characters)"
                    rows={3}
                  />
                </div>

                <div>
                  <Label>Upload CSV File</Label>
                  <div className="mt-2 flex items-center gap-3">
                    <Input
                      type="file"
                      accept=".csv"
                      onChange={handleFileUpload}
                      className="flex-1"
                    />
                    <Button variant="outline" size="icon">
                      <Upload className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    CSV Format: userId,amount or email,amount (one per line,
                    header row required)
                  </p>
                </div>

                {csvAdjustments.length > 0 && (
                  <div className="p-3 border rounded-lg bg-success/10">
                    <p className="text-sm">
                      <CheckCircle className="h-4 w-4 inline mr-2 text-success" />
                      Loaded <strong>{csvAdjustments.length}</strong>{' '}
                      adjustments from CSV
                    </p>
                  </div>
                )}
              </>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={() => setShowPreview(true)}
                disabled={
                  (inputMode === 'manual' && selectedUserIds.length === 0) ||
                  (inputMode === 'csv' && csvAdjustments.length === 0) ||
                  amount <= 0 ||
                  description.length < 10
                }
              >
                Preview Changes
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
